import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DeadlineListResponse,
  FilingDeadlineListParams,
  CreateManualDeadlineRequest,
  CreateManualDeadlineResponse,
  FinanceHealthDto,
  ObligationsResponse,
  RecognizedReportTypeDto,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, type ApiErrorBody } from './apiFetch';
import type { CurrentRead } from '@signalvote/data';
import { financeReadKeys, queryKeys } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery } from '../../lib/data';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

// Re-export types for consumer convenience
export type {
  DeadlineListResponse,
  FilingDeadlineListParams,
  FinanceHealthDto,
  ObligationsResponse,
};

// ============================================================================
// Deadline List
// ============================================================================

function buildDeadlineListPath(params: FilingDeadlineListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.status) searchParams.set('status', params.status);
  if (params.electionType) searchParams.set('electionType', params.electionType);
  return `/api/finance/deadlines?${searchParams.toString()}`;
}

export function useFinanceDeadlines(params: FilingDeadlineListParams) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.deadlines.list(params),
    queryFn: () =>
      apiJson<DeadlineListResponse>(buildDeadlineListPath(params), {
        error: 'Failed to fetch deadlines',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}

// ============================================================================
// Create Manual Reporting Period (#132 / #121)
//
// POST /api/finance/deadlines/manual — the treasurer hand-enters a coverage
// window + due date + optional report code for a jurisdiction whose calendar the
// app can't fully auto-generate (VA's election-relative periods). The server
// validates contiguity + non-overlap and returns a `ManualPeriod*` error code on a
// 400 (`code`, beside the sentence in `error`); the hook surfaces it as `CreateManualDeadlineError.code` so the form maps
// it to treasurer copy via `manualDeadlineErrorCopy`.
// ============================================================================

/** A manual-deadline validation refusal (400) carrying its `ManualPeriod*` error code. */
export class CreateManualDeadlineError extends Error {
  constructor(public readonly code: string, message?: string) {
    super(message ?? code);
    this.name = 'CreateManualDeadlineError';
  }
}

export function useCreateManualDeadline() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    // The period is always created on 200; the envelope also carries a non-blocking
    // `reportCodeWarning` (#220) the form surfaces without failing the create.
    mutationFn: (request: CreateManualDeadlineRequest): Promise<CreateManualDeadlineResponse> =>
      apiJson<CreateManualDeadlineResponse>('/api/finance/deadlines/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: request,
        // Carry the server's wire code so the form renders `manualDeadlineErrorCopy`
        // — never a raw HTTP failure or a hardcoded string. The body can be `null`.
        error: (body: ApiErrorBody | null) =>
          new CreateManualDeadlineError(body?.code ?? 'ManualPeriodUnknown', body?.error),
      }),
    onSuccess: () => {
      // A new period changes the deadline calendar and the report list (a report can
      // now be prepared for it); refresh both.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.deadlines.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
    },
  });

  return { createManualDeadline: mutateAsync };
}

// ============================================================================
// Recognized report types (#220) — jurisdiction reference data driving the
// manual reporting-period code picker (code + treasurer-facing label). Empty
// for a jurisdiction that publishes no code list (VA), so the field degrades to
// a plain free-text box. Keyed by ruleset code (global reference data, not
// campaign-scoped); only fetched once a ruleset is known.
// ============================================================================

export function useRecognizedReportTypes(rulesetCode: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.finance.rulesets.reportTypes(rulesetCode ?? ''),
    queryFn: () =>
      apiJson<RecognizedReportTypeDto[]>(`/api/finance/rulesets/${rulesetCode}/report-types`, {
        error: 'Failed to fetch recognized report types',
      }),
    enabled: authService.isAuthenticated() && !!rulesetCode,
    staleTime: FINANCE_STALE_TIME,
  });
}

// ============================================================================
// Health Bar
// ============================================================================

const useFinanceHealthRead = defineQuery({
  key: financeReadKeys.health,
  reads: 'GET /api/finance/health',
  fetch: (_params: undefined, { signal, campaignId }) =>
    apiJson<FinanceHealthDto>('/api/finance/health', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch finance health',
    }),
  staleTime: FINANCE_STALE_TIME, // refreshes on mutation via explicit invalidation
});

export function useFinanceHealth(): CurrentRead<FinanceHealthDto> {
  return useFinanceHealthRead(undefined);
}

// ============================================================================
// Obligation Bar (FIP-002)
// ============================================================================

export function useObligations() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.obligations(),
    queryFn: () =>
      apiJson<ObligationsResponse>('/api/finance/obligations', { error: 'Failed to fetch obligations' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}
